/**
 * Parser for browser bookmark HTML files (Netscape Bookmark format)
 * Supports Chrome, Firefox, Edge, Safari bookmark exports
 */

/**
 * Parse a bookmark HTML file and return a tree structure
 * @param {string} htmlContent - Raw HTML content of the bookmarks file
 * @returns {Object} Parsed bookmark tree with folders and bookmarks
 */
export function parseBookmarksHtml(htmlContent) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')

  const rootDL = doc.querySelector('DL')
  if (!rootDL) {
    throw new Error('Invalid bookmark file format: No bookmark list found')
  }

  return {
    id: 'root',
    title: 'Bookmarks',
    type: 'folder',
    children: parseBookmarkList(rootDL),
    expanded: true
  }
}

/**
 * Recursively parse a <DL> element containing bookmarks and folders
 * @param {Element} dlElement - The <DL> element to parse
 * @returns {Array} Array of bookmark/folder objects
 */
function parseBookmarkList(dlElement) {
  const items = []
  let idCounter = 0

  const children = dlElement.children
  for (let i = 0; i < children.length; i++) {
    const child = children[i]

    if (child.tagName === 'DT') {
      const h3 = child.querySelector(':scope > H3')
      const anchor = child.querySelector(':scope > A')
      const nestedDL = child.querySelector(':scope > DL')

      if (h3) {
        // This is a folder
        const folder = {
          id: `folder-${Date.now()}-${idCounter++}`,
          title: h3.textContent.trim(),
          type: 'folder',
          children: nestedDL ? parseBookmarkList(nestedDL) : [],
          expanded: false,
          addDate: h3.getAttribute('ADD_DATE'),
          lastModified: h3.getAttribute('LAST_MODIFIED')
        }
        items.push(folder)
      } else if (anchor) {
        // This is a bookmark
        const bookmark = {
          id: `bookmark-${Date.now()}-${idCounter++}`,
          title: anchor.textContent.trim(),
          url: anchor.getAttribute('HREF'),
          type: 'bookmark',
          addDate: anchor.getAttribute('ADD_DATE'),
          icon: anchor.getAttribute('ICON'),
          tags: anchor.getAttribute('TAGS')
        }
        items.push(bookmark)
      }
    }
  }

  return items
}

/**
 * Flatten the bookmark tree into a searchable array
 * @param {Object} tree - The bookmark tree
 * @param {string} path - Current folder path
 * @returns {Array} Flat array of all bookmarks with their paths
 */
export function flattenBookmarks(tree, path = '') {
  const results = []
  const currentPath = path ? `${path} / ${tree.title}` : tree.title

  if (tree.type === 'bookmark') {
    results.push({
      ...tree,
      path: path || 'Root'
    })
  }

  if (tree.children) {
    for (const child of tree.children) {
      results.push(...flattenBookmarks(child, currentPath))
    }
  }

  return results
}

/**
 * Search bookmarks by title or URL
 * @param {Array} flatBookmarks - Flattened bookmark array
 * @param {string} query - Search query
 * @returns {Array} Matching bookmarks
 */
export function searchBookmarks(flatBookmarks, query) {
  if (!query.trim()) {
    return flatBookmarks
  }

  const lowerQuery = query.toLowerCase()
  return flatBookmarks.filter(bookmark =>
    bookmark.title.toLowerCase().includes(lowerQuery) ||
    bookmark.url?.toLowerCase().includes(lowerQuery) ||
    bookmark.path?.toLowerCase().includes(lowerQuery)
  )
}

/**
 * Get statistics about the bookmark tree
 * @param {Object} tree - The bookmark tree
 * @returns {Object} Statistics object
 */
export function getBookmarkStats(tree) {
  let bookmarkCount = 0
  let folderCount = 0

  function countItems(node) {
    if (node.type === 'bookmark') {
      bookmarkCount++
    } else if (node.type === 'folder') {
      folderCount++
      if (node.children) {
        node.children.forEach(countItems)
      }
    }
  }

  if (tree.children) {
    tree.children.forEach(countItems)
  }

  return { bookmarkCount, folderCount }
}

/**
 * Extract domain from URL
 * @param {string} url - Full URL
 * @returns {string} Domain name
 */
export function extractDomain(url) {
  try {
    const urlObj = new URL(url)
    return urlObj.hostname
  } catch {
    return url
  }
}
